import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { BACKEND_URL } from '../config';
import { 
  ShoppingCart, Home, ShoppingBag, Phone, Store, Frown, Package, Tag, 
  MessageCircle, Search, BookOpen, MapPin, Truck, PersonStanding, DollarSign,
  Star, CheckCircle, Clock, Sparkles, Info
} from 'lucide-react';

// Hook personnalisé pour l'animation au scroll
const useInView = (options = {}) => {
  const ref = useRef(null);
  const [isInView, setIsInView] = useState(false);
  
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsInView(true);
        if (options.once !== false) {
          observer.disconnect();
        }
      } else if (options.once === false) {
        setIsInView(false);
      }
    }, { threshold: options.threshold || 0.1, ...options });
    
    const currentRef = ref.current;
    if (currentRef) {
      observer.observe(currentRef);
    }
    
    return () => observer.disconnect();
  }, [options.once, options.threshold, options]);
  
  return [ref, isInView];
};

// Composant AnimatedSection pour les sections
const AnimatedSection = ({ children, delay = 0, className = '', direction = 'up' }) => {
  const [ref, isInView] = useInView({ threshold: 0.1 });
  
  const getTransform = () => {
    if (isInView) return 'translateY(0) translateX(0)';
    switch (direction) {
      case 'up': return 'translateY(40px)';
      case 'down': return 'translateY(-40px)';
      case 'left': return 'translateX(40px)';
      case 'right': return 'translateX(-40px)';
      default: return 'translateY(40px)';
    }
  };
  
  return (
    <div
      ref={ref}
      className={`transition-all duration-700 ease-out ${className}`}
      style={{
        opacity: isInView ? 1 : 0,
        transform: getTransform(),
        transitionDelay: `${delay}ms`
      }}
    >
      {children}
    </div>
  );
};

// Composant Boutons Flottants
const FloatingButtons = ({ shop, styles, cart, setShowCart }) => {
  const [showScrollTop, setShowScrollTop] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 400);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const getPositionClasses = () => {
    switch (styles.floatingPosition) {
      case 'bottom-left': return 'left-4 bottom-4 flex-col';
      case 'bottom-center': return 'left-1/2 -translate-x-1/2 bottom-4 flex-row';
      case 'side-right': return 'right-4 top-1/2 -translate-y-1/2 flex-col';
      default: return 'right-4 bottom-4 flex-col';
    }
  };

  const totalItems = cart.reduce((sum, item) => sum + item.quantity, 0);

  return (
    <div className={`fixed z-40 flex gap-3 ${getPositionClasses()}`}>
      {/* Bouton WhatsApp */}
      {styles.floatingWhatsapp && shop?.whatsapp && (
        <a
          href={`https://wa.me/${shop.whatsapp.replace(/[^0-9]/g, '')}`}
          target="_blank"
          rel="noopener noreferrer"
          className="group w-14 h-14 rounded-full bg-green-500 text-white flex items-center justify-center shadow-lg hover:shadow-xl hover:scale-110 transition-all duration-300"
          style={{ boxShadow: '0 4px 20px rgba(34,197,94,0.4)' }}
        >
          <svg className="w-7 h-7 group-hover:animate-bounce" fill="currentColor" viewBox="0 0 24 24">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
          </svg>
        </a>
      )}

      {/* Bouton Panier Flottant */}
      {styles.floatingCart && totalItems > 0 && (
        <button
          onClick={() => setShowCart(true)}
          className="group w-14 h-14 rounded-full text-white flex items-center justify-center shadow-lg hover:shadow-xl hover:scale-110 transition-all duration-300 relative"
          style={{ 
            backgroundColor: styles.primaryColor,
            boxShadow: `0 4px 20px ${styles.primaryColor}40`
          }}
        >
          <ShoppingCart className="w-6 h-6 group-hover:animate-bounce" />
          <span className="absolute -top-1 -right-1 w-6 h-6 bg-red-500 rounded-full text-white text-xs font-bold flex items-center justify-center animate-pulse">
            {totalItems}
          </span>
        </button>
      )}

      {/* Bouton Scroll to Top */}
      {styles.scrollToTop && showScrollTop && (
        <button
          onClick={scrollToTop}
          className="w-12 h-12 rounded-full bg-gray-800/80 backdrop-blur-sm text-white flex items-center justify-center shadow-lg hover:shadow-xl hover:scale-110 hover:bg-gray-700 transition-all duration-300"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
          </svg>
        </button>
      )}
    </div>
  );
};

// Mapping des styles de cartes
const CARD_STYLES = {
  rounded: 'rounded-xl',
  square: 'rounded-none',
  soft: 'rounded-2xl',
  pill: 'rounded-3xl'
};

// Mapping des styles de boutons
const BUTTON_STYLES = {
  rounded: 'rounded-lg',
  square: 'rounded-none',
  pill: 'rounded-full'
};

// Classes responsive pour le nombre de colonnes
const GRID_COLS_CLASS = {
  2: 'sm:grid-cols-2',
  3: 'sm:grid-cols-3',
  4: 'sm:grid-cols-3 lg:grid-cols-4',
  5: 'sm:grid-cols-3 lg:grid-cols-5',
  6: 'sm:grid-cols-3 lg:grid-cols-6'
};

const PublicShop = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [shop, setShop] = useState(null);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [cart, setCart] = useState([]);
  const [showCart, setShowCart] = useState(false);
  
  // Nouveaux states pour les modals
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [quantityToAdd, setQuantityToAdd] = useState(1);
  const [lightboxImage, setLightboxImage] = useState(null);
  
  // Navigation multi-pages
  const [activePage, setActivePage] = useState('home');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [categoryDropdownOpen, setCategoryDropdownOpen] = useState(false);
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const productsPerPage = 12;

  // Obtenir les styles personnalisés du shop (recalculé quand shop change)
  const styles = useMemo(() => {
    if (!shop) return {
      primaryColor: '#4F46E5',
      secondaryColor: '#7C3AED',
      accentColor: '#10B981',
      textColor: '#1F2937',
      backgroundColor: '#F9FAFB',
      cardStyle: CARD_STYLES.rounded,
      buttonStyle: BUTTON_STYLES.rounded,
      fontFamily: 'Inter',
      headerStyle: 'classic',
      gridCols: 4,
      gridColsClass: 'sm:grid-cols-3 lg:grid-cols-4',
      showSearch: true,
      showCategoryFilter: true,
      showBanner: true,
      showDescription: true,
      showContact: true,
      showStock: false
    };
    return {
      primaryColor: shop.primary_color || '#4F46E5',
      secondaryColor: shop.secondary_color || '#7C3AED',
      accentColor: shop.accent_color || '#10B981',
      textColor: shop.text_color || '#1F2937',
      backgroundColor: shop.background_color || '#F9FAFB',
      cardStyle: CARD_STYLES[shop.card_style] || CARD_STYLES.rounded,
      buttonStyle: BUTTON_STYLES[shop.button_style] || BUTTON_STYLES.rounded,
      fontFamily: shop.font_family || 'Inter',
      headerStyle: shop.header_style || 'classic',
      gridCols: shop.product_grid_cols || 4,
      gridColsClass: GRID_COLS_CLASS[shop.product_grid_cols] || GRID_COLS_CLASS[4],
      showSearch: parseInt(shop.show_search) === 1,
      showCategoryFilter: parseInt(shop.show_category_filter) === 1,
      showBanner: parseInt(shop.show_banner) === 1,
      showDescription: parseInt(shop.show_description) === 1,
      showContact: parseInt(shop.show_contact) === 1,
      showStock: parseInt(shop.show_stock) === 1,
      // Nouvelles options de disposition
      floatingWhatsapp: parseInt(shop.floating_whatsapp) === 1,
      floatingCart: parseInt(shop.floating_cart) === 1,
      scrollToTop: parseInt(shop.scroll_to_top) === 1,
      stickyCategories: parseInt(shop.sticky_categories) === 1,
      modalAnimation: shop.modal_animation || 'scale',
      floatingPosition: shop.floating_position || 'bottom-right'
    };
  }, [shop]);

  useEffect(() => {
    loadShop();
    // Charger le panier du localStorage
    const savedCart = localStorage.getItem(`cart_${slug}`);
    if (savedCart) {
      setCart(JSON.parse(savedCart));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  // Sauvegarder le panier
  useEffect(() => {
    if (cart.length > 0) {
      localStorage.setItem(`cart_${slug}`, JSON.stringify(cart));
    } else {
      localStorage.removeItem(`cart_${slug}`);
    }
  }, [cart, slug]);

  const loadShop = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/boutique/${slug}`);
      const data = await res.json();
      
      if (data.success) {
        setShop(data.shop);
        setProducts(data.products || []);
        setCategories(data.categories || []);
        
        // Vérifier si la boutique est indisponible
        if (data.unavailable) {
          setError(data.unavailable_reason || 'unavailable');
        }
      } else {
        setError(data.message || 'Boutique non trouvée');
      }
    } catch (err) {
      console.error(err);
      setError('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const getImageUrl = (path) => {
    if (!path) return null;
    if (path.startsWith('http')) return path;
    return `${BACKEND_URL}/${path}`;
  };

  // Ouvrir le modal de sélection de quantité
  const openProductModal = (product) => {
    if (product.stock_actuel <= 0) return;
    setSelectedProduct(product);
    // Vérifier si déjà dans le panier
    const existing = cart.find(item => item.product_id === product.id);
    setQuantityToAdd(existing ? 1 : 1);
  };

  // Fermer le modal produit
  const closeProductModal = () => {
    setSelectedProduct(null);
    setQuantityToAdd(1);
  };

  // Confirmer l'ajout au panier
  const confirmAddToCart = () => {
    if (!selectedProduct) return;
    
    setCart(prev => {
      const existing = prev.find(item => item.product_id === selectedProduct.id);
      if (existing) {
        const newQty = Math.min(existing.quantity + quantityToAdd, selectedProduct.stock_actuel);
        return prev.map(item => 
          item.product_id === selectedProduct.id 
            ? { ...item, quantity: newQty }
            : item
        );
      }
      return [...prev, {
        product_id: selectedProduct.id,
        product_name: selectedProduct.nom,
        product_image: selectedProduct.image,
        product_price: selectedProduct.prix_vente,
        quantity: Math.min(quantityToAdd, selectedProduct.stock_actuel),
        max_stock: selectedProduct.stock_actuel
      }];
    });
    
    closeProductModal();
    setShowCart(true);
    setTimeout(() => setShowCart(false), 2000);
  };

  const removeFromCart = (productId) => {
    setCart(prev => prev.filter(item => item.product_id !== productId));
  };

  const getCartTotal = () => {
    return cart.reduce((sum, item) => sum + (item.product_price * item.quantity), 0);
  };

  const getCartCount = () => {
    return cart.reduce((sum, item) => sum + item.quantity, 0);
  };

  // Navigation pages - filtré selon les options de visibilité
  const navPages = useMemo(() => {
    const pages = [
      { id: 'home', label: 'Accueil', icon: <Home className="w-4 h-4" /> },
      { id: 'shop', label: 'Boutique', icon: <ShoppingBag className="w-4 h-4" /> },
      { id: 'about', label: 'À propos', icon: <Info className="w-4 h-4" /> },
    ];
    // Ajouter Contact seulement si showContact est activé
    if (styles.showContact) {
      pages.push({ id: 'contact', label: 'Contact', icon: <Phone className="w-4 h-4" /> });
    }
    return pages;
  }, [styles.showContact]);

  const navigateTo = (pageId) => {
    setActivePage(pageId);
    setMobileMenuOpen(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Produits vedettes (les 4 premiers ou ceux avec le plus de stock)
  const featuredProducts = useMemo(() => {
    return [...products]
      .sort((a, b) => b.stock_actuel - a.stock_actuel)
      .slice(0, 4);
  }, [products]);

  const filteredProducts = products.filter(product => {
    const matchesCategory = selectedCategory === 'all' || product.categorie === selectedCategory;
    const matchesSearch = !searchQuery || 
      product.nom.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (product.description && product.description.toLowerCase().includes(searchQuery.toLowerCase()));
    return matchesCategory && matchesSearch;
  });

  // Pagination des produits
  const totalPages = Math.ceil(filteredProducts.length / productsPerPage);
  const startIndex = (currentPage - 1) * productsPerPage;
  const paginatedProducts = filteredProducts.slice(startIndex, startIndex + productsPerPage);

  // Reset page quand les filtres changent
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, selectedCategory]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100 flex flex-col items-center justify-center overflow-hidden">
        {/* Background animé */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-br from-indigo-200/40 to-purple-200/40 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-gradient-to-br from-pink-200/40 to-orange-200/40 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
        </div>
        
        <div className="relative z-10">
          {/* Cercles concentriques animés */}
          <div className="relative">
            <div className="absolute inset-0 -m-8 border-4 border-indigo-100 rounded-full animate-ping opacity-30"></div>
            <div className="absolute inset-0 -m-4 border-2 border-indigo-200 rounded-full animate-pulse"></div>
            <div className="w-24 h-24 rounded-2xl bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 flex items-center justify-center shadow-2xl shadow-indigo-300/50 relative overflow-hidden">
              {/* Effet de brillance */}
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent -translate-x-full animate-shimmer"></div>
              <Store className="w-12 h-12 drop-shadow-lg text-white" />
            </div>
          </div>
          
          {/* Spinner moderne */}
          <div className="absolute -bottom-2 -right-2 w-8 h-8 bg-white rounded-full shadow-lg flex items-center justify-center">
            <svg className="w-5 h-5 animate-spin text-indigo-600" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          </div>
        </div>
        
        {/* Texte de chargement avec dots animés */}
        <div className="mt-10 text-center">
          <p className="text-lg text-gray-600 font-medium">Chargement de la boutique</p>
          <div className="flex justify-center gap-1 mt-2">
            <span className="w-2 h-2 bg-indigo-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
            <span className="w-2 h-2 bg-indigo-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
            <span className="w-2 h-2 bg-indigo-600 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
          </div>
        </div>
        
        {/* Style pour l'animation shimmer */}
        <style>{`
          @keyframes shimmer {
            100% { transform: translateX(200%); }
          }
          .animate-shimmer {
            animation: shimmer 2s infinite;
          }
        `}</style>
      </div>
    );
  }

  // Page d'indisponibilité (abonnement expiré ou dette plateforme)
  if ((error === 'subscription_expired' || error === 'platform_debt' || error === 'unavailable') && shop) {
    return (
      <div 
        className="min-h-screen flex flex-col"
        style={{ 
          backgroundColor: styles.backgroundColor,
          color: styles.textColor
        }}
      >
        <div className="flex-1 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl shadow-2xl p-8 md:p-12 text-center max-w-lg border border-gray-100">
            {/* Logo de la boutique */}
            {shop.logo && (
              <div className="w-24 h-24 mx-auto mb-6 rounded-2xl overflow-hidden shadow-lg ring-4 ring-gray-100">
                <img 
                  src={getImageUrl(shop.logo)} 
                  alt={shop.nom}
                  className="w-full h-full object-cover"
                />
              </div>
            )}
            {!shop.logo && (
              <div 
                className="w-24 h-24 mx-auto mb-6 rounded-2xl flex items-center justify-center"
                style={{ backgroundColor: `${styles.primaryColor}20` }}
              >
                <Store className="w-12 h-12" style={{ color: styles.primaryColor }} />
              </div>
            )}
            
            <h1 className="text-2xl md:text-3xl font-bold text-gray-800 mb-2">{shop.nom}</h1>
            
            {shop.description && (
              <p className="text-gray-500 mb-6 line-clamp-2">{shop.description}</p>
            )}
            
            {/* Badge indisponible */}
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-100 text-amber-800 rounded-full font-medium mb-6">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <span>Boutique temporairement indisponible</span>
            </div>
            
            {/* Message explicatif */}
            <div className="bg-gray-50 rounded-2xl p-6 mb-8">
              <p className="text-gray-600 leading-relaxed">
                Cette boutique est actuellement indisponible. 
                Le propriétaire a été notifié et la boutique sera de nouveau accessible prochainement.
              </p>
            </div>
            
            {/* Actions */}
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link 
                to="/boutiques" 
                className="inline-flex items-center justify-center gap-2 px-6 py-3 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl hover:scale-105 transition-all"
                style={{ backgroundColor: styles.primaryColor }}
              >
                <Store className="w-5 h-5" /> Découvrir d'autres boutiques
              </Link>
              <Link 
                to="/" 
                className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-gray-100 text-gray-700 font-medium rounded-xl hover:bg-gray-200 transition"
              >
                ← Retour à l'accueil
              </Link>
            </div>
          </div>
        </div>
        
        {/* Mini footer */}
        <div className="text-center py-4 text-sm opacity-50">
          Propulsé par DJAMANN
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-3xl shadow-xl p-8 text-center max-w-md border border-gray-100">
          <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-red-100 to-orange-100 flex items-center justify-center">
            <Frown className="w-10 h-10 text-gray-400" />
          </div>
          <h1 className="text-2xl font-bold text-gray-800 mb-2">Boutique introuvable</h1>
          <p className="text-gray-500 mb-6">{error}</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link 
              to="/boutiques" 
              className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl hover:scale-105 transition-all"
            >
              <Store className="w-5 h-5" /> Voir les boutiques
            </Link>
            <Link 
              to="/" 
              className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-gray-100 text-gray-700 font-medium rounded-xl hover:bg-gray-200 transition"
            >
              ← Retour à l'accueil
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="min-h-screen"
      style={{ 
        backgroundColor: styles.backgroundColor,
        fontFamily: styles.fontFamily,
        color: styles.textColor
      }}
    >
      {/* Google Fonts */}
      <link 
        href={`https://fonts.googleapis.com/css2?family=${styles.fontFamily?.replace(' ', '+')}:wght@400;500;600;700&display=swap`} 
        rel="stylesheet" 
      />
      
      {/* Animations CSS globales */}
      <style>{`
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(30px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes fadeInDown {
          from { opacity: 0; transform: translateY(-30px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes scaleIn {
          from { opacity: 0; transform: scale(0.9); }
          to { opacity: 1; transform: scale(1); }
        }
        @keyframes slideInRight {
          from { opacity: 0; transform: translateX(50px); }
          to { opacity: 1; transform: translateX(0); }
        }
        @keyframes float {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-10px); }
        }
        @keyframes shimmer {
          100% { transform: translateX(200%); }
        }
        @keyframes gradient-shift {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }
        @keyframes pulse-ring {
          0% { transform: scale(0.8); opacity: 1; }
          100% { transform: scale(1.5); opacity: 0; }
        }
        .animate-fadeInUp { animation: fadeInUp 0.6s ease-out forwards; }
        .animate-fadeInDown { animation: fadeInDown 0.6s ease-out forwards; }
        .animate-scaleIn { animation: scaleIn 0.5s ease-out forwards; }
        .animate-slideInRight { animation: slideInRight 0.6s ease-out forwards; }
        .animate-float { animation: float 3s ease-in-out infinite; }
        .animate-shimmer { animation: shimmer 2s infinite; }
        .animate-gradient { animation: gradient-shift 8s ease infinite; background-size: 200% 200%; }
        .hover-lift { transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1); }
        .hover-lift:hover { transform: translateY(-8px); box-shadow: 0 20px 40px rgba(0,0,0,0.15); }
        .hover-glow:hover { box-shadow: 0 0 30px var(--glow-color, rgba(79, 70, 229, 0.4)); }
        .card-3d { transition: transform 0.5s ease; transform-style: preserve-3d; }
        .card-3d:hover { transform: perspective(1000px) rotateX(2deg) rotateY(-2deg) translateZ(10px); }
      `}</style>
      
      {/* Custom CSS */}
      {shop.custom_css && (
        <style dangerouslySetInnerHTML={{ __html: shop.custom_css }} />
      )}

      {/* ====== BARRE DE NAVIGATION FIXE ====== */}
      <nav 
        className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl border-b shadow-sm"
        style={{ 
          backgroundColor: `${styles.backgroundColor}E6`,
          borderColor: `${styles.primaryColor}20` 
        }}
      >
        {/* Bandeau d'annonce */}
        {shop.announcement_text && (
          <div 
            className="py-2 px-4 text-center font-medium text-sm overflow-hidden relative"
            style={{ backgroundColor: shop.announcement_bg_color || '#FEF3C7' }}
          >
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full animate-shimmer"></div>
            <span className="relative z-10 flex items-center justify-center gap-2">
              <Sparkles className="w-4 h-4 animate-pulse" />
              {shop.announcement_text}
              <Sparkles className="w-4 h-4 animate-pulse" />
            </span>
          </div>
        )}

        <div className="max-w-6xl mx-auto px-4">
          <div className="flex items-center justify-between h-16">
            {/* Logo & Nom */}
            <button 
              onClick={() => navigateTo('home')}
              className="flex items-center gap-3 hover:opacity-80 transition"
            >
              <div 
                className={`w-10 h-10 overflow-hidden flex items-center justify-center ${styles.cardStyle}`}
                style={{ backgroundColor: `${styles.primaryColor}10` }}
              >
                {shop.logo ? (
                  <img src={getImageUrl(shop.logo)} alt={shop.nom} className="w-full h-full object-cover" />
                ) : (
                  <Store className="w-5 h-5" style={{ color: styles.primaryColor }} />
                )}
              </div>
              <span className="font-bold text-lg hidden sm:block" style={{ color: styles.textColor }}>
                {shop.nom}
              </span>
            </button>

            {/* Navigation Desktop */}
            <div className="hidden md:flex items-center gap-1">
              {navPages.map(page => (
                <button
                  key={page.id}
                  onClick={() => navigateTo(page.id)}
                  className={`px-4 py-2 font-medium transition-all duration-200 flex items-center gap-2 ${styles.buttonStyle} ${
                    activePage === page.id 
                      ? 'text-white shadow-lg' 
                      : ''
                  }`}
                  style={activePage === page.id ? { 
                    backgroundColor: styles.primaryColor,
                    boxShadow: `0 4px 15px ${styles.primaryColor}40`
                  } : { 
                    color: styles.textColor,
                    backgroundColor: `${styles.primaryColor}10`
                  }}
                >
                  {page.icon}
                  <span>{page.label}</span>
                </button>
              ))}
            </div>

            {/* Actions: Recherche + Panier + Menu Mobile */}
            <div className="flex items-center gap-2">
              {/* Bouton Recherche (va à la boutique) */}
              <button
                onClick={() => navigateTo('shop')}
                className="p-2 rounded-lg hover:bg-gray-100 transition"
                style={{ color: styles.textColor }}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </button>

              {/* Panier */}
              <button
                onClick={() => setShowCart(true)}
                className="relative p-2 rounded-lg hover:bg-gray-100 transition"
                style={{ color: styles.textColor }}
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
                {getCartCount() > 0 && (
                  <span 
                    className="absolute -top-1 -right-1 w-5 h-5 text-white text-xs font-bold rounded-full flex items-center justify-center animate-pulse"
                    style={{ backgroundColor: styles.accentColor }}
                  >
                    {getCartCount()}
                  </span>
                )}
              </button>

              {/* Menu Mobile Toggle */}
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="md:hidden p-2 rounded-lg hover:bg-gray-100 transition"
                style={{ color: styles.textColor }}
              >
                {mobileMenuOpen ? (
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                ) : (
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                )}
              </button>
            </div>
          </div>

          {/* Menu Mobile */}
          {mobileMenuOpen && (
            <div className="md:hidden py-4 border-t animate-fadeInDown" style={{ borderColor: `${styles.primaryColor}20` }}>
              <div className="flex flex-col gap-2">
                {navPages.map(page => (
                  <button
                    key={page.id}
                    onClick={() => navigateTo(page.id)}
                    className={`px-4 py-3 font-medium transition-all flex items-center gap-3 ${styles.buttonStyle} ${
                      activePage === page.id 
                        ? 'text-white shadow-lg' 
                        : ''
                    }`}
                    style={activePage === page.id ? { 
                      backgroundColor: styles.primaryColor,
                      boxShadow: `0 4px 15px ${styles.primaryColor}40`
                    } : { 
                      color: styles.textColor,
                      backgroundColor: `${styles.primaryColor}08`
                    }}
                  >
                    <span className="w-5 h-5 flex items-center justify-center">{page.icon}</span>
                    <span>{page.label}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Spacer pour la nav fixe */}
      <div className={shop.announcement_text ? 'h-28' : 'h-16'}></div>

      {/* ====== PAGE: ACCUEIL ====== */}
      {activePage === 'home' && (
        <div className="animate-fadeInUp">
          {/* Hero Section - Style selon headerStyle */}
          
          {/* STYLE: HERO (Grande bannière plein écran) */}
          {styles.headerStyle === 'hero' && (
            <div className="relative min-h-[70vh] md:min-h-[80vh]">
              {shop.banner ? (
                <div className="absolute inset-0">
                  <img src={getImageUrl(shop.banner)} alt="Bannière" className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-black/20"></div>
                </div>
              ) : (
                <div className="absolute inset-0" style={{ background: `linear-gradient(135deg, ${styles.primaryColor}, ${styles.secondaryColor})` }}>
                  <div className="absolute inset-0 opacity-10" style={{
                    backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M11 18c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm48 25c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm-43-7c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm63 31c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM34 90c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm56-76c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM12 86c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm28-65c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm23-11c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-6 60c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm29 22c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zM32 63c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm57-13c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-9-21c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM60 91c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM35 41c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2z' fill='%23ffffff' fill-opacity='1' fill-rule='evenodd'/%3E%3C/svg%3E")`
                  }}></div>
                </div>
              )}
              <div className="absolute inset-0 z-20 flex items-center justify-center">
                <div className="text-center text-white px-4 max-w-3xl">
                  {shop.logo && (
                    <div className="w-28 h-28 md:w-36 md:h-36 mx-auto mb-8 rounded-3xl overflow-hidden shadow-2xl ring-4 ring-white/30 bg-white">
                      <img src={getImageUrl(shop.logo)} alt={shop.nom} className="w-full h-full object-cover" />
                    </div>
                  )}
                  <h1 className="text-5xl md:text-7xl font-bold mb-6 drop-shadow-2xl">{shop.nom}</h1>

                  <button onClick={() => navigateTo('shop')} className={`px-10 py-5 text-xl font-bold text-white shadow-2xl hover:scale-105 transition-all duration-300 ${styles.buttonStyle}`} style={{ backgroundColor: styles.accentColor, boxShadow: `0 20px 60px ${styles.accentColor}80` }}>
                    <ShoppingBag className="w-5 h-5 inline mr-2" /> Explorer la boutique
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* STYLE: MINIMAL (Logo et titre uniquement) */}
          {styles.headerStyle === 'minimal' && (
            <div className="py-12 md:py-20 px-4" style={{ backgroundColor: `${styles.primaryColor}08` }}>
              <div className="max-w-4xl mx-auto text-center">
                <div className="flex flex-col md:flex-row items-center justify-center gap-6 md:gap-8">
                  {shop.logo && (
                    <div className={`w-20 h-20 md:w-24 md:h-24 overflow-hidden shadow-xl ${styles.cardStyle}`} style={{ backgroundColor: styles.primaryColor }}>
                      <img src={getImageUrl(shop.logo)} alt={shop.nom} className="w-full h-full object-cover" />
                    </div>
                  )}
                  <div className="text-center md:text-left">
                    <h1 className="text-3xl md:text-5xl font-bold mb-2" style={{ color: styles.textColor }}>{shop.nom}</h1>
                    {shop.description && <p className="text-lg opacity-70 max-w-xl" style={{ color: styles.textColor }}>{shop.description}</p>}
                  </div>
                </div>
                <button onClick={() => navigateTo('shop')} className={`mt-8 px-8 py-4 text-lg font-bold text-white shadow-lg hover:scale-105 transition-all ${styles.buttonStyle}`} style={{ backgroundColor: styles.primaryColor }}>
                  Voir les produits →
                </button>
              </div>
            </div>
          )}

          {/* STYLE: CENTERED (Logo centré avec bannière) */}
          {styles.headerStyle === 'centered' && (
            <div className="relative">
              <div className="h-40 md:h-52" style={{ background: `linear-gradient(135deg, ${styles.primaryColor}, ${styles.secondaryColor})` }}></div>
              <div className="max-w-4xl mx-auto px-4 -mt-16 md:-mt-20 relative z-10 text-center">
                <div className={`w-32 h-32 md:w-40 md:h-40 mx-auto overflow-hidden shadow-2xl border-4 border-white ${styles.cardStyle}`} style={{ backgroundColor: 'white' }}>
                  {shop.logo ? (
                    <img src={getImageUrl(shop.logo)} alt={shop.nom} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center" style={{ background: `linear-gradient(135deg, ${styles.primaryColor}20, ${styles.secondaryColor}20)` }}>
                      <Store className="w-16 h-16" style={{ color: styles.primaryColor }} />
                    </div>
                  )}
                </div>
                <h1 className="text-3xl md:text-4xl font-bold mt-6 mb-3" style={{ color: styles.textColor }}>{shop.nom}</h1>
                {shop.description && <p className="text-lg opacity-70 mb-6 max-w-xl mx-auto" style={{ color: styles.textColor }}>{shop.description}</p>}
                <button onClick={() => navigateTo('shop')} className={`px-8 py-4 text-lg font-bold text-white shadow-xl hover:scale-105 transition-all flex items-center justify-center gap-2 ${styles.buttonStyle}`} style={{ backgroundColor: styles.accentColor }}>
                  <ShoppingBag className="w-5 h-5" /> Découvrir nos produits
                </button>
              </div>
            </div>
          )}

          {/* STYLE: CLASSIC (Par défaut - Bannière avec logo superposé) */}
          {(styles.headerStyle === 'classic' || !styles.headerStyle) && (
            <div className="relative">
              {shop.banner && styles.showBanner ? (
                <div className="h-64 md:h-96 w-full overflow-hidden">
                  <img src={getImageUrl(shop.banner)} alt="Bannière" className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent"></div>
                </div>
              ) : (
                <div className="h-64 md:h-96 relative overflow-hidden" style={{ background: `linear-gradient(135deg, ${styles.primaryColor}, ${styles.secondaryColor})` }}>
                  <div className="absolute inset-0 opacity-20 animate-gradient" style={{
                    backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
                  }}></div>
                </div>
              )}
              <div className="absolute inset-0 z-20 flex items-center justify-center">
                <div className="text-center text-white px-4 max-w-2xl">
                  <div className="mb-6">
                    {shop.logo ? (
                      <div className={`w-24 h-24 mx-auto overflow-hidden shadow-2xl ring-4 ring-white/30 bg-white ${styles.cardStyle}`}>
                        <img src={getImageUrl(shop.logo)} alt={shop.nom} className="w-full h-full object-cover" />
                      </div>
                    ) : (
                      <div className={`w-24 h-24 mx-auto bg-white/20 backdrop-blur-sm flex items-center justify-center ${styles.cardStyle}`}>
                        <Store className="w-12 h-12 text-white" />
                      </div>
                    )}
                  </div>
                  <h1 className="text-4xl md:text-5xl font-bold mb-4 drop-shadow-lg">{shop.nom}</h1>
                  {shop.description && styles.showDescription && <p className="text-lg md:text-xl opacity-90 mb-6 line-clamp-2">{shop.description}</p>}
                  <button onClick={() => navigateTo('shop')} className={`px-8 py-4 text-lg font-bold text-white shadow-2xl hover:scale-105 transition-all duration-300 ${styles.buttonStyle}`} style={{ backgroundColor: styles.accentColor, boxShadow: `0 10px 40px ${styles.accentColor}60` }}>
                    <ShoppingBag className="w-5 h-5 inline mr-2" /> Découvrir nos produits
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Stats rapides */}
          <div className={`max-w-5xl mx-auto px-4 relative z-10 ${styles.headerStyle === 'hero' ? 'py-12' : styles.headerStyle === 'minimal' ? 'py-8' : '-mt-8'}`}>
            <div className="grid grid-cols-3 gap-4">
              {[
                { label: 'Produits', value: products.length, icon: <Package className="w-6 h-6 md:w-8 md:h-8" style={{ color: styles.primaryColor }} /> },
                { label: 'Catégories', value: categories.length, icon: <Tag className="w-6 h-6 md:w-8 md:h-8" style={{ color: styles.primaryColor }} /> },
                { label: 'En stock', value: products.filter(p => p.stock_actuel > 0).length, icon: <CheckCircle className="w-6 h-6 md:w-8 md:h-8" style={{ color: styles.primaryColor }} /> }
              ].map((stat, idx) => (
                <div 
                  key={idx}
                  className={`bg-white p-4 md:p-6 text-center shadow-xl ${styles.cardStyle}`}
                  style={{ animationDelay: `${idx * 100}ms` }}
                >
                  <span className="mb-2 block">{stat.icon}</span>
                  <div className="text-2xl md:text-3xl font-bold" style={{ color: styles.primaryColor }}>{stat.value}</div>
                  <div className="text-xs md:text-sm text-gray-500">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Produits vedettes */}
          {featuredProducts.length > 0 && (
            <div className="max-w-5xl mx-auto px-4 py-16">
              <div className="text-center mb-10">
                <h2 className="text-3xl font-bold mb-2 flex items-center justify-center gap-2" style={{ color: styles.textColor }}><Star className="w-8 h-8 text-yellow-500 fill-yellow-500" /> Produits vedettes</h2>
                <p className="text-gray-500">Découvrez nos meilleurs articles</p>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
                {featuredProducts.map((product, idx) => (
                  <AnimatedSection key={product.id} delay={idx * 100}>
                    <div 
                      className={`product-card group bg-white overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 cursor-pointer ${styles.cardStyle}`}
                      onClick={() => openProductModal(product)}
                    >
                      <div className="aspect-square overflow-hidden relative">
                        {product.image ? (
                          <img 
                            src={getImageUrl(product.image)} 
                            alt={product.nom}
                            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                          />
                        ) : (
                          <div className="w-full h-full bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center">
                            <Package className="w-12 h-12 opacity-30" style={{ color: styles.primaryColor }} />
                          </div>
                        )}
                        {product.stock_actuel <= 0 && (
                          <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                            <span className="bg-red-500 text-white px-4 py-2 rounded-full text-sm font-bold">Épuisé</span>
                          </div>
                        )}
                      </div>
                      <div className="p-4">
                        <h3 className="font-semibold text-sm mb-1 line-clamp-1" style={{ color: styles.textColor }}>{product.nom}</h3>
                        <p className="text-lg font-bold" style={{ color: styles.primaryColor }}>
                          {parseFloat(product.prix_vente).toLocaleString()} {shop.currency}
                        </p>
                      </div>
                    </div>
                  </AnimatedSection>
                ))}
              </div>

              <div className="text-center mt-10">
                <button
                  onClick={() => navigateTo('shop')}
                  className={`px-8 py-3 font-semibold text-white transition-all hover:scale-105 ${styles.buttonStyle}`}
                  style={{ backgroundColor: styles.primaryColor }}
                >
                  Voir tous les produits →
                </button>
              </div>
            </div>
          )}

          {/* Section À propos rapide */}
          {shop.description && (
            <div className="py-16 px-4" style={{ backgroundColor: `${styles.primaryColor}05` }}>
              <div className="max-w-3xl mx-auto text-center">
                <h2 className="text-2xl font-bold mb-6" style={{ color: styles.textColor }}>À propos de nous</h2>
                <p className="text-lg leading-relaxed opacity-80 mb-8">{shop.description}</p>
                <button
                  onClick={() => navigateTo('about')}
                  className="text-sm font-semibold underline hover:no-underline transition"
                  style={{ color: styles.primaryColor }}
                >
                  En savoir plus →
                </button>
              </div>
            </div>
          )}

          {/* CTA Contact - affiché seulement si contact ou whatsapp disponible */}
          {(styles.showContact || shop.whatsapp) && (
            <div className="max-w-5xl mx-auto px-4 py-16">
              <div 
                className={`p-8 md:p-12 text-center ${styles.cardStyle}`}
                style={{ background: `linear-gradient(135deg, ${styles.primaryColor}, ${styles.secondaryColor})` }}
              >
                <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">Une question ?</h2>
                <p className="text-white/80 mb-8">N'hésitez pas à nous contacter pour plus d'informations</p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  {styles.showContact && (
                    <button
                      onClick={() => navigateTo('contact')}
                      className={`px-8 py-3 bg-white font-semibold transition-all hover:scale-105 ${styles.buttonStyle}`}
                      style={{ color: styles.primaryColor }}
                    >
                      <Phone className="w-5 h-5 inline mr-2" /> Nous contacter
                    </button>
                  )}
                  {shop.whatsapp && (
                    <a
                      href={`https://wa.me/${shop.whatsapp.replace(/[^0-9]/g, '')}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`px-8 py-3 bg-green-500 text-white font-semibold transition-all hover:scale-105 hover:bg-green-600 ${styles.buttonStyle}`}
                    >
                      <MessageCircle className="w-5 h-5 inline mr-2" /> WhatsApp
                    </a>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ====== PAGE: BOUTIQUE ====== */}
      {activePage === 'shop' && (
        <div className="max-w-6xl mx-auto px-4 py-8 animate-fadeInUp">
          {/* En-tête */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold mb-2 flex items-center gap-2" style={{ color: styles.textColor }}><ShoppingBag className="w-8 h-8" /> Notre boutique</h1>
            <p className="text-gray-500">{products.length} produit{products.length > 1 ? 's' : ''} disponible{products.length > 1 ? 's' : ''}</p>
          </div>

          {/* Barre de recherche et filtres - Sticky sous la navbar */}
          <div 
            className={`bg-white/95 backdrop-blur-md p-4 md:p-6 shadow-lg mb-8 ${styles.cardStyle} sticky top-16 z-40`}
            style={{ backgroundColor: `${styles.backgroundColor}F2` }}
          >
            <div className="flex flex-col md:flex-row gap-4">
              {/* Recherche */}
              {styles.showSearch && (
                <div className="flex-1 relative">
                  <svg className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Rechercher un produit..."
                    className={`w-full pl-12 pr-4 py-3 border-2 focus:outline-none transition ${styles.cardStyle}`}
                    style={{ 
                      borderColor: searchQuery ? styles.primaryColor : '#e5e7eb',
                      boxShadow: searchQuery ? `0 0 0 3px ${styles.primaryColor}20` : 'none'
                    }}
                  />
                </div>
              )}

              {/* Filtres catégories - Dropdown */}
              {styles.showCategoryFilter && categories.length > 0 && (
                <div className="relative">
                  <button
                    onClick={() => setCategoryDropdownOpen(!categoryDropdownOpen)}
                    className={`flex items-center gap-2 px-4 py-3 font-medium transition-all ${styles.cardStyle} ${
                      selectedCategory !== 'all' 
                        ? 'text-white' 
                        : 'bg-gray-100 hover:bg-gray-200'
                    }`}
                    style={selectedCategory !== 'all' ? { backgroundColor: styles.primaryColor } : {}}
                  >
                    <Tag className="w-4 h-4" />
                    <span>{selectedCategory === 'all' ? 'Toutes les catégories' : selectedCategory}</span>
                    <svg 
                      className={`w-4 h-4 transition-transform duration-200 ${categoryDropdownOpen ? 'rotate-180' : ''}`} 
                      fill="none" 
                      stroke="currentColor" 
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  
                  {/* Menu déroulant */}
                  {categoryDropdownOpen && (
                    <div 
                      className={`absolute top-full left-0 mt-2 min-w-[200px] bg-white shadow-xl border z-30 overflow-hidden ${styles.cardStyle}`}
                      style={{ borderColor: `${styles.primaryColor}20` }}
                    >
                      <button
                        onClick={() => { setSelectedCategory('all'); setCategoryDropdownOpen(false); }}
                        className={`w-full px-4 py-3 text-left text-sm font-medium flex items-center justify-between transition-all ${
                          selectedCategory === 'all' 
                            ? 'text-white' 
                            : 'hover:bg-gray-50'
                        }`}
                        style={selectedCategory === 'all' ? { backgroundColor: styles.primaryColor } : {}}
                      >
                        <span>Toutes les catégories</span>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${selectedCategory === 'all' ? 'bg-white/20' : 'bg-gray-100'}`}>
                          {products.length}
                        </span>
                      </button>
                      {categories.map(cat => (
                        <button
                          key={cat}
                          onClick={() => { setSelectedCategory(cat); setCategoryDropdownOpen(false); }}
                          className={`w-full px-4 py-3 text-left text-sm font-medium flex items-center justify-between transition-all ${
                            selectedCategory === cat 
                              ? 'text-white' 
                              : 'hover:bg-gray-50'
                          }`}
                          style={selectedCategory === cat ? { backgroundColor: styles.primaryColor } : {}}
                        >
                          <span>{cat}</span>
                          <span className={`text-xs px-2 py-0.5 rounded-full ${selectedCategory === cat ? 'bg-white/20' : 'bg-gray-100'}`}>
                            {products.filter(p => p.categorie === cat).length}
                          </span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Grille des produits */}
          {paginatedProducts.length > 0 ? (
            <>
              <div className={`grid grid-cols-2 ${styles.gridColsClass} gap-4 md:gap-6`}>
                {paginatedProducts.map((product, idx) => (
                  <AnimatedSection key={product.id} delay={idx * 50} className="product-card group">
                    <div 
                      className={`bg-white overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 cursor-pointer h-full flex flex-col ${styles.cardStyle}`}
                      onClick={() => openProductModal(product)}
                    >
                      {/* Image */}
                      <div className="aspect-square overflow-hidden relative">
                        {product.image ? (
                          <img 
                            src={getImageUrl(product.image)} 
                            alt={product.nom}
                            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                            loading="lazy"
                          />
                        ) : (
                          <div className="w-full h-full bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center">
                            <Package className="w-16 h-16 opacity-30" style={{ color: styles.primaryColor }} />
                          </div>
                        )}
                        
                        {/* Badge catégorie */}
                        {product.categorie && (
                          <span 
                            className="absolute top-2 left-2 px-2 py-1 text-xs font-medium rounded-full text-white"
                            style={{ backgroundColor: `${styles.primaryColor}CC` }}
                          >
                            {product.categorie}
                          </span>
                        )}
                        
                        {/* Badge épuisé */}
                        {product.stock_actuel <= 0 && (
                          <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                            <span className="bg-red-500 text-white px-4 py-2 rounded-full text-sm font-bold">Épuisé</span>
                          </div>
                        )}
                        
                        {/* Bouton rapide */}
                        {product.stock_actuel > 0 && (
                          <button
                            onClick={(e) => { e.stopPropagation(); openProductModal(product); }}
                            className="shop-btn absolute bottom-3 right-3 w-12 h-12 rounded-full text-white shadow-lg opacity-0 group-hover:opacity-100 transform translate-y-2 group-hover:translate-y-0 transition-all duration-300 flex items-center justify-center"
                            style={{ backgroundColor: styles.accentColor }}
                          >
                            <span className="text-xl">+</span>
                          </button>
                        )}
                      </div>
                      
                      {/* Infos */}
                      <div className="p-4 flex-1 flex flex-col">
                        <h3 className="font-semibold mb-1 line-clamp-2 flex-1" style={{ color: styles.textColor }}>
                          {product.nom}
                        </h3>
                        <div className="flex items-end justify-between mt-2">
                          <p className="text-xl font-bold" style={{ color: styles.primaryColor }}>
                            {parseFloat(product.prix_vente).toLocaleString()} <span className="text-sm">{shop.currency}</span>
                          </p>
                          {styles.showStock && product.stock_actuel > 0 && (
                            <span className="text-xs text-gray-400">
                              Stock: {product.stock_actuel}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </AnimatedSection>
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex justify-center items-center gap-2 mt-10">
                  <button
                    onClick={() => setCurrentPage(1)}
                    disabled={currentPage === 1}
                    className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition"
                    title="Première page"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7M18 19l-7-7 7-7" />
                    </svg>
                  </button>
                  <button
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>
                  
                  {(() => {
                    const pages = [];
                    const showPages = new Set();
                    
                    showPages.add(1);
                    showPages.add(totalPages);
                    for (let i = Math.max(1, currentPage - 1); i <= Math.min(totalPages, currentPage + 1); i++) {
                      showPages.add(i);
                    }
                    
                    const sortedPages = [...showPages].sort((a, b) => a - b);
                    
                    sortedPages.forEach((page, idx) => {
                      if (idx > 0 && page - sortedPages[idx - 1] > 1) {
                        pages.push(
                          <span key={`ellipsis-${page}`} className="px-2 text-gray-400">...</span>
                        );
                      }
                      pages.push(
                        <button
                          key={page}
                          onClick={() => setCurrentPage(page)}
                          className={`w-10 h-10 rounded-lg font-medium transition-all ${
                            currentPage === page 
                              ? 'text-white shadow-lg' 
                              : 'hover:bg-gray-100'
                          }`}
                          style={currentPage === page ? { backgroundColor: styles.primaryColor } : {}}
                        >
                          {page}
                        </button>
                      );
                    });
                    
                    return pages;
                  })()}
                  
                  <button
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                  <button
                    onClick={() => setCurrentPage(totalPages)}
                    disabled={currentPage === totalPages}
                    className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition"
                    title="Dernière page"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M6 5l7 7-7 7" />
                    </svg>
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-20">
              <Search className="w-16 h-16 mb-4 mx-auto" style={{ color: styles.primaryColor }} />
              <h3 className="text-xl font-semibold mb-2">Aucun produit trouvé</h3>
              <p className="text-gray-500 mb-6">Essayez avec d'autres termes de recherche</p>
              <button
                onClick={() => { setSearchQuery(''); setSelectedCategory('all'); }}
                className={`px-6 py-2 font-medium text-white ${styles.buttonStyle}`}
                style={{ backgroundColor: styles.primaryColor }}
              >
                Réinitialiser les filtres
              </button>
            </div>
          )}
        </div>
      )}

      {/* ====== PAGE: À PROPOS ====== */}
      {activePage === 'about' && (
        <div className="max-w-4xl mx-auto px-4 py-12 animate-fadeInUp">
          {/* En-tête */}
          <div className="text-center mb-12">
            <div className="w-32 h-32 mx-auto mb-6 rounded-2xl overflow-hidden shadow-2xl">
              {shop.logo ? (
                <img src={getImageUrl(shop.logo)} alt={shop.nom} className="w-full h-full object-cover" />
              ) : (
                <div 
                  className="w-full h-full flex items-center justify-center"
                  style={{ background: `linear-gradient(135deg, ${styles.primaryColor}, ${styles.secondaryColor})` }}
                >
                  <Store className="w-12 h-12 text-white" />
                </div>
              )}
            </div>
            <h1 className="text-4xl font-bold mb-4" style={{ color: styles.textColor }}>{shop.nom}</h1>
            <div 
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-white text-sm"
              style={{ backgroundColor: styles.accentColor }}
            >
              <span className="w-2 h-2 bg-white rounded-full animate-pulse"></span>
              Boutique active
            </div>
          </div>

          {/* Description */}
          {shop.description && (
            <div className={`bg-white p-8 shadow-xl mb-8 ${styles.cardStyle}`}>
              <h2 className="text-xl font-bold mb-4 flex items-center gap-3" style={{ color: styles.textColor }}>
                <BookOpen className="w-6 h-6" style={{ color: styles.primaryColor }} /> Notre histoire
              </h2>
              <p className="text-lg leading-relaxed opacity-80">{shop.description}</p>
            </div>
          )}

          {/* Statistiques */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            {[
              { label: 'Produits', value: products.length, icon: <Package className="w-8 h-8" style={{ color: styles.primaryColor }} /> },
              { label: 'Catégories', value: categories.length, icon: <Tag className="w-8 h-8" style={{ color: styles.primaryColor }} /> },
              { label: 'En stock', value: products.filter(p => p.stock_actuel > 0).length, icon: <CheckCircle className="w-8 h-8 text-green-500" /> },
              { label: 'Épuisés', value: products.filter(p => p.stock_actuel <= 0).length, icon: <Clock className="w-8 h-8 text-orange-500" /> }
            ].map((stat, idx) => (
              <div 
                key={idx}
                className={`bg-white p-6 text-center shadow-lg ${styles.cardStyle}`}
              >
                <span className="mb-2 block flex justify-center">{stat.icon}</span>
                <div className="text-2xl font-bold" style={{ color: styles.primaryColor }}>{stat.value}</div>
                <div className="text-sm text-gray-500">{stat.label}</div>
              </div>
            ))}
          </div>

          {/* Catégories */}
          {categories.length > 0 && (
            <div className={`bg-white p-8 shadow-xl mb-8 ${styles.cardStyle}`}>
              <h2 className="text-xl font-bold mb-4 flex items-center gap-3" style={{ color: styles.textColor }}>
                <Tag className="w-6 h-6" style={{ color: styles.primaryColor }} /> Nos catégories
              </h2>
              <div className="flex flex-wrap gap-3">
                {categories.map(cat => (
                  <button
                    key={cat}
                    onClick={() => { setSelectedCategory(cat); navigateTo('shop'); }}
                    className={`px-4 py-2 rounded-full font-medium transition-all hover:scale-105 ${styles.buttonStyle}`}
                    style={{ 
                      backgroundColor: `${styles.primaryColor}15`,
                      color: styles.primaryColor
                    }}
                  >
                    {cat}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* CTA */}
          <div className="text-center">
            <button
              onClick={() => navigateTo('shop')}
              className={`px-8 py-4 text-white font-bold text-lg transition-all hover:scale-105 ${styles.buttonStyle}`}
              style={{ 
                backgroundColor: styles.primaryColor,
                boxShadow: `0 10px 30px ${styles.primaryColor}40`
              }}
            >
              <ShoppingBag className="w-5 h-5 inline mr-2" /> Découvrir nos produits
            </button>
          </div>
        </div>
      )}

      {/* ====== PAGE: CONTACT ====== */}
      {activePage === 'contact' && (
        <div className="max-w-4xl mx-auto px-4 py-12 animate-fadeInUp">
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold mb-4 flex items-center justify-center gap-2" style={{ color: styles.textColor }}><Phone className="w-10 h-10" /> Contactez-nous</h1>
            <p className="text-gray-500 text-lg">N'hésitez pas à nous joindre pour toute question</p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Infos de contact */}
            <div className={`bg-white p-8 shadow-xl ${styles.cardStyle}`}>
              <h2 className="text-xl font-bold mb-6" style={{ color: styles.textColor }}>Nos coordonnées</h2>
              
              <div className="space-y-6">
                {shop.phone && (
                  <a 
                    href={`tel:${shop.phone}`}
                    className={`flex items-center gap-4 p-4 transition-all hover:scale-[1.02] hover:shadow-md ${styles.cardStyle}`}
                    style={{ backgroundColor: `${styles.primaryColor}08` }}
                  >
                    <div 
                      className="w-14 h-14 rounded-full flex items-center justify-center text-2xl"
                      style={{ backgroundColor: `${styles.primaryColor}15` }}
                    >
                      <Phone className="w-6 h-6" style={{ color: styles.primaryColor }} />
                    </div>
                    <div>
                      <p className="text-xs uppercase tracking-wider opacity-50 font-medium">Téléphone</p>
                      <p className="font-bold text-lg" style={{ color: styles.primaryColor }}>{shop.phone}</p>
                    </div>
                  </a>
                )}

                {shop.whatsapp && (
                  <a 
                    href={`https://wa.me/${shop.whatsapp.replace(/[^0-9]/g, '')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`flex items-center gap-4 p-4 bg-green-50 transition-all hover:scale-[1.02] hover:shadow-md hover:bg-green-100 ${styles.cardStyle}`}
                  >
                    <div className="w-14 h-14 rounded-full bg-green-100 flex items-center justify-center">
                      <svg className="w-7 h-7 text-green-600" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                      </svg>
                    </div>
                    <div>
                      <p className="text-xs uppercase tracking-wider text-green-600/60 font-medium">WhatsApp</p>
                      <p className="font-bold text-lg text-green-700">{shop.whatsapp}</p>
                    </div>
                    <svg className="w-6 h-6 ml-auto text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                    </svg>
                  </a>
                )}

                {shop.address && (
                  <div 
                    className={`flex items-center gap-4 p-4 ${styles.cardStyle}`}
                    style={{ backgroundColor: `${styles.primaryColor}05` }}
                  >
                    <div 
                      className="w-14 h-14 rounded-full flex items-center justify-center text-2xl"
                      style={{ backgroundColor: `${styles.primaryColor}10` }}
                    >
                      <MapPin className="w-6 h-6" style={{ color: styles.primaryColor }} />
                    </div>
                    <div>
                      <p className="text-xs uppercase tracking-wider opacity-50 font-medium">Adresse</p>
                      <p className="font-medium" style={{ color: styles.textColor }}>{shop.address}</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Réseaux sociaux */}
              {(shop.social_facebook || shop.social_instagram || shop.social_tiktok) && (
                <div className="mt-8 pt-6 border-t">
                  <p className="text-sm font-medium text-gray-500 mb-4">Suivez-nous</p>
                  <div className="flex gap-3">
                    {shop.social_facebook && (
                      <a 
                        href={shop.social_facebook} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="w-12 h-12 rounded-full bg-blue-600 text-white flex items-center justify-center hover:bg-blue-700 transition shadow-lg hover:shadow-xl hover:scale-110"
                      >
                        <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                        </svg>
                      </a>
                    )}
                    {shop.social_instagram && (
                      <a 
                        href={shop.social_instagram} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="w-12 h-12 rounded-full flex items-center justify-center hover:scale-110 transition shadow-lg hover:shadow-xl"
                        style={{ background: 'linear-gradient(45deg, #f09433 0%, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%)' }}
                      >
                        <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                        </svg>
                      </a>
                    )}
                    {shop.social_tiktok && (
                      <a 
                        href={shop.social_tiktok} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="w-12 h-12 rounded-full bg-black text-white flex items-center justify-center hover:bg-gray-800 transition shadow-lg hover:shadow-xl hover:scale-110"
                      >
                        <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z"/>
                        </svg>
                      </a>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Carte visuelle / Horaires */}
            <div className={`bg-white p-8 shadow-xl ${styles.cardStyle}`}>
              <h2 className="text-xl font-bold mb-6" style={{ color: styles.textColor }}>Informations</h2>
              
              <div 
                className={`p-6 text-center mb-6 ${styles.cardStyle}`}
                style={{ background: `linear-gradient(135deg, ${styles.primaryColor}10, ${styles.secondaryColor}10)` }}
              >
                {/* Logo de la boutique */}
                <div className="w-20 h-20 mx-auto mb-4 rounded-2xl overflow-hidden shadow-lg">
                  {shop.logo ? (
                    <img src={getImageUrl(shop.logo)} alt={shop.nom} className="w-full h-full object-cover" />
                  ) : (
                    <div 
                      className="w-full h-full flex items-center justify-center"
                      style={{ background: `linear-gradient(135deg, ${styles.primaryColor}, ${styles.secondaryColor})` }}
                    >
                      <Store className="w-8 h-8 text-white" />
                    </div>
                  )}
                </div>
                <h3 className="font-bold text-lg mb-2" style={{ color: styles.textColor }}>{shop.nom}</h3>
                <p className="text-sm opacity-70">Boutique en ligne propulsée par DJAMANN</p>
              </div>

              {/* Options de livraison */}
              <div className="space-y-4">
                <h3 className="font-semibold flex items-center gap-2">
                  <Truck className="w-5 h-5" /> Options disponibles
                </h3>
                {shop.delivery_enabled && (
                  <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                    <Package className="w-6 h-6" style={{ color: styles.primaryColor }} />
                    <div>
                      <p className="font-medium">Livraison</p>
                      <p className="text-sm text-gray-500">
                        {shop.delivery_fee > 0 ? `À partir de ${shop.delivery_fee.toLocaleString()} ${shop.currency}` : 'Gratuite'}
                      </p>
                    </div>
                  </div>
                )}
                {shop.pickup_enabled && (
                  <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                    <PersonStanding className="w-6 h-6" style={{ color: styles.primaryColor }} />
                    <div>
                      <p className="font-medium">Retrait sur place</p>
                      <p className="text-sm text-gray-500">Disponible</p>
                    </div>
                  </div>
                )}
                {shop.min_order_amount > 0 && (
                  <div className="flex items-center gap-3 p-3 bg-amber-50 rounded-lg">
                    <DollarSign className="w-6 h-6 text-amber-600" />
                    <div>
                      <p className="font-medium">Commande minimum</p>
                      <p className="text-sm text-amber-700">{shop.min_order_amount.toLocaleString()} {shop.currency}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ====== PANIER LATERAL ====== */}
      {showCart && cart.length > 0 && (
        <div 
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50"
          onClick={() => setShowCart(false)}
        >
          <div 
            className="absolute right-0 top-0 h-full w-full max-w-md bg-white shadow-2xl animate-slideInRight"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="p-5 border-b flex items-center justify-between" style={{ borderColor: `${styles.primaryColor}15` }}>
              <h2 className="text-xl font-bold flex items-center gap-2" style={{ color: styles.textColor }}>
                <ShoppingCart className="w-6 h-6" /> Votre panier
                <span className="text-sm font-normal text-gray-400">({getCartCount()} articles)</span>
              </h2>
              <button onClick={() => setShowCart(false)} className="p-2 hover:bg-gray-100 rounded-lg transition">
                <svg className="w-6 h-6 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Liste des produits */}
            <div className="flex-1 overflow-y-auto p-5 space-y-4" style={{ maxHeight: 'calc(100vh - 280px)' }}>
              {cart.map(item => (
                <div key={item.product_id} className={`flex gap-4 p-3 bg-gray-50 ${styles.cardStyle}`}>
                  <div className={`w-20 h-20 overflow-hidden flex-shrink-0 ${styles.cardStyle}`}>
                    {item.product_image ? (
                      <img src={getImageUrl(item.product_image)} alt={item.product_name} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full bg-gray-200 flex items-center justify-center"><Package className="w-6 h-6 text-gray-400" /></div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-medium text-sm line-clamp-2" style={{ color: styles.textColor }}>{item.product_name}</h3>
                    <p className="font-bold mt-1" style={{ color: styles.primaryColor }}>{item.product_price.toLocaleString()} {shop.currency}</p>
                    <div className="flex items-center gap-2 mt-2">
                      <button onClick={() => item.quantity > 1 ? setCart(prev => prev.map(i => i.product_id === item.product_id ? { ...i, quantity: i.quantity - 1 } : i)) : removeFromCart(item.product_id)} className="w-7 h-7 bg-white rounded-full shadow text-sm font-bold hover:bg-gray-100 transition">−</button>
                      <span className="text-sm font-medium w-6 text-center">{item.quantity}</span>
                      <button onClick={() => item.quantity < item.max_stock && setCart(prev => prev.map(i => i.product_id === item.product_id ? { ...i, quantity: i.quantity + 1 } : i))} className="w-7 h-7 bg-white rounded-full shadow text-sm font-bold hover:bg-gray-100 transition">+</button>
                    </div>
                  </div>
                  <button onClick={() => removeFromCart(item.product_id)} className="text-red-400 hover:text-red-600 transition p-1">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                  </button>
                </div>
              ))}
            </div>

            {/* Footer avec total */}
            <div className="absolute bottom-0 left-0 right-0 p-5 bg-white border-t shadow-lg">
              <div className="flex items-center justify-between mb-4">
                <span className="text-gray-500">Total</span>
                <span className="text-2xl font-bold" style={{ color: styles.primaryColor }}>{getCartTotal().toLocaleString()} {shop.currency}</span>
              </div>
              
              {/* Bouton principal: Passer commande */}
              <button 
                onClick={() => navigate(`/boutique/${slug}/checkout`, { state: { shop, cart } })}
                className={`w-full py-4 text-white font-bold transition flex items-center justify-center gap-2 mb-3 ${styles.buttonStyle}`} 
                style={{ backgroundColor: styles.primaryColor }}
              >
                <ShoppingCart className="w-5 h-5" /> Passer commande
              </button>
              
              <button onClick={() => setShowCart(false)} className="w-full py-3 text-gray-500 font-medium hover:text-gray-700 transition">Continuer mes achats</button>
            </div>
          </div>
        </div>
      )}

      {/* Modal sélection quantité amélioré */}
      {selectedProduct && (
        <div 
          className="fixed inset-0 bg-black/70 backdrop-blur-md flex items-end sm:items-center justify-center z-50 p-0 sm:p-4"
          onClick={closeProductModal}
        >
          <style>{`
            @keyframes modalSlideUp { 
              from { opacity: 0; transform: translateY(30px) scale(0.95); } 
              to { opacity: 1; transform: translateY(0) scale(1); } 
            }
            .animate-modal { animation: modalSlideUp 0.4s cubic-bezier(0.4, 0, 0.2, 1); }
          `}</style>
          <div 
            className={`bg-white w-full sm:max-w-md shadow-2xl animate-modal sm:${styles.cardStyle} max-h-[90vh] sm:max-h-[85vh] flex flex-col`}
            style={{ borderRadius: window.innerWidth >= 640 ? undefined : '24px 24px 0 0' }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Barre de poignée mobile avec animation */}
            <div className="sm:hidden flex justify-center py-2 flex-shrink-0">
              <div className="w-12 h-1.5 bg-gray-300 rounded-full hover:bg-gray-400 transition-colors cursor-grab"></div>
            </div>
            
            {/* Image produit avec zoom */}
            <div 
              className="relative h-40 xs:h-48 sm:h-64 bg-gradient-to-br from-gray-50 via-gray-100 to-gray-50 overflow-hidden group cursor-pointer flex-shrink-0"
              onClick={() => selectedProduct.image && setLightboxImage(getImageUrl(selectedProduct.image))}
            >
              {selectedProduct.image ? (
                <>
                  <img 
                    src={getImageUrl(selectedProduct.image)} 
                    alt={selectedProduct.nom}
                    className="w-full h-full object-contain p-3 sm:p-4 transition-transform duration-500 group-hover:scale-110"
                  />
                  {/* Icône zoom */}
                  <div className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/20 transition-colors">
                    <span className="opacity-0 group-hover:opacity-100 transition-opacity bg-white/90 p-2 sm:p-3 rounded-full shadow-lg text-sm sm:text-base">
                      <Search className="w-5 h-5" />
                    </span>
                  </div>
                </>
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <div className="text-center">
                    <Package className="w-16 h-16 sm:w-20 sm:h-20 block mb-2 animate-float" style={{ color: styles.primaryColor }} />
                    <span className="text-gray-400 text-xs sm:text-sm">Pas d'image</span>
                  </div>
                </div>
              )}
              {/* Gradient overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-white/20 to-transparent pointer-events-none"></div>
              <button
                onClick={(e) => { e.stopPropagation(); closeProductModal(); }}
                className="absolute top-2 right-2 sm:top-3 sm:right-3 bg-white hover:bg-gray-100 p-2 sm:p-2.5 rounded-full shadow-lg transition group z-10"
              >
                <span className="text-gray-500 group-hover:text-gray-700 text-sm sm:text-base">✕</span>
              </button>
            </div>
            
            {/* Contenu scrollable */}
            <div className="flex-1 overflow-y-auto min-h-0">
              {/* Infos produit */}
              <div className="p-4 sm:p-5">
                <div className="flex items-start justify-between gap-3 sm:gap-4">
                  <div className="flex-1 min-w-0">
                    <h3 className="text-base sm:text-xl font-bold leading-tight truncate" style={{ color: styles.textColor }}>{selectedProduct.nom}</h3>
                    {selectedProduct.categorie && (
                      <span className="inline-block mt-1 px-2 py-0.5 bg-gray-100 text-gray-500 text-xs rounded-full">
                        {selectedProduct.categorie}
                      </span>
                    )}
                  </div>
                  <div className="text-right flex-shrink-0">
                    <div className="text-xl sm:text-2xl font-bold" style={{ color: styles.accentColor }}>
                      {parseInt(selectedProduct.prix_vente).toLocaleString()}
                    </div>
                    <div className="text-xs sm:text-sm text-gray-400">{shop.currency}</div>
                  </div>
                </div>
                
                {selectedProduct.description && (
                  <div className="mt-3 max-h-20 sm:max-h-24 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
                    <p className="text-xs sm:text-sm text-gray-600 whitespace-pre-wrap">{selectedProduct.description}</p>
                  </div>
                )}
                
                {shop.show_stock === 1 && (
                  <div className="flex items-center gap-2 mt-3">
                    <span className={`w-2 h-2 rounded-full ${selectedProduct.stock_actuel > 5 ? 'bg-green-500' : selectedProduct.stock_actuel > 0 ? 'bg-orange-500' : 'bg-red-500'}`}></span>
                    <span className="text-xs sm:text-sm text-gray-500">
                      {selectedProduct.stock_actuel > 5 ? 'En stock' : selectedProduct.stock_actuel > 0 ? `Plus que ${selectedProduct.stock_actuel}` : 'Rupture'}
                    </span>
                  </div>
                )}
              </div>
              
              {/* Sélection quantité */}
              <div className="px-4 sm:px-5 pb-3 sm:pb-4">
                <div className="flex items-center justify-between mb-2 sm:mb-3">
                  <span className="text-xs sm:text-sm font-medium text-gray-700">Quantité</span>
                  <span className="text-xs sm:text-sm text-gray-400">Max: {selectedProduct.stock_actuel}</span>
                </div>
                <div className={`flex items-center justify-between p-1.5 sm:p-2 ${styles.cardStyle}`} style={{ backgroundColor: `${styles.primaryColor}08` }}>
                  <button
                    onClick={() => setQuantityToAdd(q => Math.max(1, q - 1))}
                    className="w-11 h-11 sm:w-14 sm:h-14 bg-white rounded-xl shadow-sm flex items-center justify-center text-xl sm:text-2xl font-bold hover:bg-gray-50 active:scale-95 transition"
                    style={{ color: styles.primaryColor }}
                  >
                    −
                  </button>
                  <div className="flex-1 text-center">
                    <input
                      type="number"
                      value={quantityToAdd}
                      onChange={(e) => setQuantityToAdd(Math.max(1, Math.min(selectedProduct.stock_actuel, parseInt(e.target.value) || 1)))}
                      className="w-16 sm:w-20 text-center text-2xl sm:text-3xl font-bold bg-transparent border-none focus:outline-none"
                      style={{ color: styles.textColor }}
                      min="1"
                      max={selectedProduct.stock_actuel}
                    />
                  </div>
                  <button
                    onClick={() => setQuantityToAdd(q => Math.min(selectedProduct.stock_actuel, q + 1))}
                    disabled={quantityToAdd >= selectedProduct.stock_actuel}
                    className="w-11 h-11 sm:w-14 sm:h-14 bg-white rounded-xl shadow-sm flex items-center justify-center text-xl sm:text-2xl font-bold hover:bg-gray-50 active:scale-95 transition disabled:opacity-40 disabled:cursor-not-allowed"
                    style={{ color: styles.primaryColor }}
                  >
                    +
                  </button>
                </div>
              </div>
            </div>
            
            {/* Bouton ajouter avec sous-total - fixé en bas */}
            <div className="p-4 sm:p-5 pt-2 border-t border-gray-100 flex-shrink-0 bg-white">
              <button
                onClick={confirmAddToCart}
                className={`w-full py-3 sm:py-4 text-white font-bold text-base sm:text-lg transition flex items-center justify-between gap-2 sm:gap-3 px-4 sm:px-6 active:scale-[0.98] ${styles.buttonStyle}`}
                style={{ backgroundColor: styles.primaryColor }}
              >
                <span className="flex items-center gap-2">
                  <ShoppingCart className="w-5 h-5" />
                  <span className="text-sm sm:text-base">Ajouter</span>
                </span>
                <span className="bg-white/20 px-2 sm:px-3 py-1 rounded-lg text-sm sm:text-base">
                  {(selectedProduct.prix_vente * quantityToAdd).toLocaleString()} {shop.currency}
                </span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Lightbox image plein écran */}
      {lightboxImage && (
        <div 
          className="fixed inset-0 bg-black/95 flex items-center justify-center z-50"
          onClick={() => setLightboxImage(null)}
        >
          <button
            onClick={() => setLightboxImage(null)}
            className="absolute top-4 right-4 text-white/80 hover:text-white bg-white/10 hover:bg-white/20 p-3 rounded-full transition-all duration-300 backdrop-blur-sm hover:scale-110 hover:rotate-90"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          <div className="relative max-w-4xl max-h-[85vh] p-4">
            <img 
              src={lightboxImage} 
              alt="Produit en grand"
              className="max-w-full max-h-[85vh] object-contain rounded-2xl shadow-2xl ring-1 ring-white/10"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
          <p className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white/50 text-sm flex items-center gap-2">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122" />
            </svg>
            Cliquez n'importe où pour fermer
          </p>
        </div>
      )}

      {/* Toast ajouté au panier amélioré */}
      {showCart && cart.length > 0 && !selectedProduct && (
        <div 
          className="fixed top-4 right-4 flex items-center gap-3 text-white px-5 py-4 shadow-2xl z-40 rounded-2xl backdrop-blur-sm"
          style={{ 
            backgroundColor: styles.accentColor,
            boxShadow: `0 10px 40px ${styles.accentColor}60`
          }}
        >
          <style>{`
            @keyframes checkmark { 0% { transform: scale(0); } 50% { transform: scale(1.2); } 100% { transform: scale(1); } }
            .animate-checkmark { animation: checkmark 0.5s cubic-bezier(0.4, 0, 0.2, 1); }
          `}</style>
          <span className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center animate-checkmark">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
            </svg>
          </span>
          <div>
            <p className="font-bold text-lg">Ajouté au panier !</p>
            <p className="text-sm opacity-80">{getCartCount()} article{getCartCount() > 1 ? 's' : ''} • {getCartTotal().toLocaleString()} {shop.currency}</p>
          </div>
        </div>
      )}

      {/* Footer moderne */}
      <AnimatedSection delay={300} className="mt-16 pb-32 px-4">
        <div className="max-w-5xl mx-auto">
          <div className="relative border-t border-gray-200 pt-10">
            {/* Décoration */}
            <div className="absolute -top-px left-1/2 -translate-x-1/2 w-20 h-1 rounded-full" style={{ background: `linear-gradient(to right, ${styles.primaryColor}, ${styles.secondaryColor})` }}></div>
            
            <div className="flex flex-col sm:flex-row items-center justify-between gap-6">
              <div className="flex items-center gap-3 text-sm text-gray-500 group">
                <ShoppingBag className="w-6 h-6 group-hover:animate-bounce transition-all" style={{ color: styles.primaryColor }} />
                <div className="flex flex-col sm:flex-row items-center gap-1 sm:gap-2">
                  <span>Boutique propulsée par</span>
                  <Link 
                    to="/boutiques" 
                    className="font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent hover:from-indigo-500 hover:to-purple-500 transition-all duration-300 text-lg"
                  >
                    DJAMANN
                  </Link>
                </div>
              </div>
              <div className="flex items-center gap-4 text-sm text-gray-400">
                <Link 
                  to="/boutiques" 
                  className="px-4 py-2 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-600 transition-all duration-300 hover:scale-105 flex items-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                  Explorer les boutiques
                </Link>
                <span className="text-gray-300">|</span>
                <span className="flex items-center gap-1">
                  <svg className="w-4 h-4 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  © {new Date().getFullYear()}
                </span>
              </div>
            </div>
          </div>
        </div>
      </AnimatedSection>

      {/* ===== BOUTONS FLOTTANTS ===== */}
      <FloatingButtons 
        shop={shop} 
        styles={styles} 
        cart={cart} 
        setShowCart={setShowCart}
      />
    </div>
  );
};

export default PublicShop;
